import { useId, useState, type PropsWithChildren, type ReactNode } from "react";

export function PageLead({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-lead">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="lead-actions">{actions}</div>}
    </header>
  );
}

export function PanelCollapseButton({
  title,
  expanded,
  controls,
  className = "",
  onToggle,
}: {
  title: string;
  expanded: boolean;
  controls: string;
  className?: string;
  onToggle(): void;
}) {
  return (
    <button
      type="button"
      className={`panel-collapse-button ${className}`.trim()}
      aria-controls={controls}
      aria-expanded={expanded}
      aria-label={`${expanded ? "Contraer" : "Expandir"} ${title}`}
      onClick={onToggle}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  );
}

export function Panel({
  title,
  meta,
  headerStart,
  headerContent,
  stickyHeader = false,
  children,
  className = "",
  mobileCollapsible = false
}: PropsWithChildren<{ title?: string; meta?: ReactNode; headerStart?: ReactNode; headerContent?: ReactNode; stickyHeader?: boolean; className?: string; mobileCollapsible?: boolean }>) {
  const [mobileCollapsed, setMobileCollapsed] = useState(false);
  const contentId = useId();
  const hasHeader = Boolean(title || meta || headerStart || mobileCollapsible);
  const header = (hasHeader || headerContent) && (
    <div className={`panel-composed-header${stickyHeader ? " panel-composed-header--sticky" : ""}`}>
      {hasHeader && (
        <header className="panel-header">
          {headerStart ? <div className="panel-header__title">{headerStart}{title && <h2>{title}</h2>}</div> : title && <h2>{title}</h2>}
          {meta && <span>{meta}</span>}
          {mobileCollapsible && title && (
            <PanelCollapseButton
              title={title}
              expanded={!mobileCollapsed}
              controls={contentId}
              className="panel-mobile-collapse"
              onToggle={() => setMobileCollapsed((collapsed) => !collapsed)}
            />
          )}
        </header>
      )}
      {headerContent}
    </div>
  );
  return (
    <section className={`panel ${className}${mobileCollapsible ? " panel--mobile-collapsible" : ""}${mobileCollapsed ? " panel--mobile-collapsed" : ""}`}>
      {header}
      {mobileCollapsible ? <div id={contentId} className="panel-mobile-content">{children}</div> : children}
    </section>
  );
}

export function Empty({ children }: PropsWithChildren) {
  return <div className="empty-inline">{children}</div>;
}
